import { useCallback, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { IDoesFilterPassParams } from 'ag-grid-community';
import { AgDateCondition } from '../types';

type Params = {
  field: string;
  primaryCondition: AgDateCondition;
  secondaryCondition: AgDateCondition;
  operator: 'and' | 'or';
  filterValueTransform?: (value: string) => string;
};

const useFEGridConfig = ({ field, primaryCondition, secondaryCondition, operator, filterValueTransform }: Params) => {
  // Local states for the "confirmed" filter conditions:
  // used to apply the filter only after pressing the OK button,
  // instead of immediately when changing dates in the picker
  const [primary, setConfirmedPrimary] = useState(primaryCondition);
  const [secondary, setConfirmedSecondary] = useState(secondaryCondition);
  const [confirmedOperator, setConfirmedOperator] = useState(operator);

  const isFilterActive = useCallback(() => {
    return !!primary.dateFrom || !!secondary.dateFrom;
  }, [primary.dateFrom, secondary.dateFrom]);

  const evaluateCondition = useCallback((cellMoment: Moment, cond?: AgDateCondition) => {
    if (!cond || (!cond.dateFrom && !cond.dateTo)) return null;

    const from = cond.dateFrom ? moment(cond.dateFrom) : null;
    const to = cond.dateTo ? moment(cond.dateTo) : null;

    switch (cond.type) {
      case 'beforeOrOn':
        if (to) return cellMoment.isSameOrBefore(to, 'day');
        if (from) return cellMoment.isSameOrBefore(from, 'day');
        return null;

      case 'afterOrOn':
        if (from) return cellMoment.isSameOrAfter(from, 'day');
        if (to) return cellMoment.isSameOrAfter(to, 'day');
        return null;

      case 'inRange':
        if (from && to) {
          return cellMoment.isBetween(from.startOf('day'), to.endOf('day'), null, '[]');
        }
        if (from) return cellMoment.isSameOrAfter(from, 'day');
        if (to) return cellMoment.isSameOrBefore(to, 'day');
        return null;

      case 'withInMonth':
        return from ? cellMoment.isSame(from, 'month') : null;

      case 'withInYear':
        return from ? cellMoment.isSame(from, 'year') : null;

      default:
        return null;
    }
  }, []);

  const doesFilterPass = useCallback(
    (params: IDoesFilterPassParams) => {
      const rawValue = params.data?.[field];
      if (!rawValue) return false;

      const parsedValue = filterValueTransform ? filterValueTransform(rawValue) : rawValue;
      const cellMoment = moment(parsedValue);
      if (!cellMoment.isValid()) return false;

      const pResult = evaluateCondition(cellMoment, primary);
      const sResult = evaluateCondition(cellMoment, secondary);

      if (pResult === null && sResult === null) return true;

      if (confirmedOperator === 'and') {
        if (pResult === null) return !!sResult;
        if (sResult === null) return !!pResult;
        return pResult && sResult;
      }

      return !!pResult || !!sResult;
    },
    [evaluateCondition, field, confirmedOperator, primary, secondary, filterValueTransform],
  );

  return useMemo(
    () => ({
      isFilterActive,
      doesFilterPass,
      setConfirmedPrimary,
      setConfirmedSecondary,
      setConfirmedOperator,
    }),
    [doesFilterPass, isFilterActive],
  );
};

export default useFEGridConfig;
